import React from 'react';
import { useParams } from 'react-router-dom';
import {
  ChatWidgetContextProvider,
  MessageList,
  useChatWidgetContext,
} from 'features/nekst-widgets';
import ConversationsSidebar from './ConversationsSidebar';
import FullPageComposer from './FullPageComposer';
import useSidebarWidth from './useSidebarWidth';
import pageStyles from './LlmChatPage.module.scss';

function ConversationArea() {
  const { isLoadingConversation } = useChatWidgetContext();

  if (isLoadingConversation) {
    return (
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
          Loading conversation...
        </div>
      </div>
    );
  }

  return (
    <div className={`relative flex-1 min-w-0 ${pageStyles.conversationArea}`}>
      <MessageList />
      <div className={pageStyles.composerOverlay}>
        <div className="max-w-[1200px] mx-auto w-full" style={{ padding: '0 16px 16px' }}>
          <FullPageComposer />
          <p className="text-gray-400 text-center" style={{ fontSize: '12px', padding: '4px 0' }}>
            Nekst AI can make mistakes. Consider checking important information.
          </p>
        </div>
      </div>
    </div>
  );
}

function ResizeHandle(props: {
  isDragging: boolean;
  onDragStart: (e: React.MouseEvent) => void;
}) {
  return (
    <div
      role="separator"
      style={{
        width: '4px',
        cursor: 'col-resize',
        flexShrink: 0,
        backgroundColor: props.isDragging ? '#1585E2' : 'transparent',
        transition: props.isDragging ? 'none' : 'background-color 0.15s',
      }}
      className="hover:bg-gray-300"
      onMouseDown={props.onDragStart}
    />
  );
}

function LlmChatContent() {
  const { width, isDragging, onDragStart } = useSidebarWidth();

  return (
    <div
      className="fixed inset-0 flex bg-white"
      style={{
        top: '65px',
        zIndex: 1,
        userSelect: isDragging ? 'none' : undefined,
      }}
      data-testid="llm-chat-page"
    >
      <div
        className="flex-shrink-0 border-r border-gray-200 bg-gray-50"
        style={{ width: `${width}px` }}
      >
        <ConversationsSidebar />
      </div>
      <ResizeHandle isDragging={isDragging} onDragStart={onDragStart} />
      <ConversationArea />
    </div>
  );
}

export default function LlmChatPage() {
  const { chatId } = useParams<{ chatId: string }>();

  return (
    <ChatWidgetContextProvider
      conversationId={chatId ? parseInt(chatId, 10) : undefined}
    >
      <LlmChatContent />
    </ChatWidgetContextProvider>
  );
}
